import React, { useState, useEffect } from 'react';
import { DesktopService } from '../../services/desktopService';
import { motion, AnimatePresence } from 'framer-motion';

const RefreshIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>;
const WindowIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>;
const RocketIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.59 8.16a6 6 0 001.37-2.37A6 6 0 0115.59 14.37z" /></svg>;
const CameraIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>;

const PRESET_MACROS = {
    'Google Chrome': [
        { label: 'New Incognito (localhost:3000)', actions: [{ type: 'hotkey', keys: ['command', 'shift', 'n'] }, { type: 'sleep', duration: 1.0 }, { type: 'type', text: 'localhost:3000' }, { type: 'press', key: 'enter' }] }
    ],
    'Safari': [
        { label: 'New Private Window (localhost:3000)', actions: [{ type: 'hotkey', keys: ['command', 'shift', 'n'] }, { type: 'sleep', duration: 1.0 }, { type: 'type', text: 'localhost:3000' }, { type: 'press', key: 'enter' }] }
    ],
    'Finder': [
        { label: 'New Window', actions: [{ type: 'hotkey', keys: ['command', 'n'] }] }
    ]
};

const LiveInsightControl = ({ targetUid, targetDeviceId, onCommandSent }) => {
    const [windows, setWindows] = useState([]);
    const [isLoadingWindows, setIsLoadingWindows] = useState(false);
    const [appToOpen, setAppToOpen] = useState('');
    const [isOpening, setIsOpening] = useState(false);
    const [focusedApp, setFocusedApp] = useState(null);
    const [customText, setCustomText] = useState('');
    const [isSendingMacro, setIsSendingMacro] = useState(false);

    const fetchWindows = async () => {
        setIsLoadingWindows(true);
        try {
            const taskId = await DesktopService.sendDesktopCommand(targetUid, targetDeviceId, {
                command: 'get_windows'
            });
            
            await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    unsubscribe();
                    reject(new Error('Timeout fetching windows'));
                }, 15000);

                const unsubscribe = DesktopService.subscribeToTask(targetUid, targetDeviceId, taskId, (response) => {
                    clearTimeout(timeout);
                    unsubscribe();
                    
                    if (typeof response === 'string' && response.startsWith('Error:')) {
                        reject(new Error(response));
                        return;
                    }

                    try {
                        const windowList = JSON.parse(response);
                        setWindows(windowList);
                        resolve();
                    } catch (e) {
                        console.error("Failed to parse windows response:", e);
                        reject(e);
                    }
                });
            });
        } catch (err) {
            console.error("Failed to fetch windows:", err);
        } finally {
            setIsLoadingWindows(false);
        }
    };

    useEffect(() => {
        fetchWindows();
    }, [targetUid, targetDeviceId]);

    const handleFocusWindow = async (app_name) => {
        try {
            setFocusedApp(app_name);
            await DesktopService.sendDesktopCommand(targetUid, targetDeviceId, {
                command: 'focus_window',
                app_name
            });
            if (onCommandSent) onCommandSent();
        } catch (err) {
            console.error("Failed to focus window:", err);
        }
    };

    const handleOpenApp = async (e) => {
        e.preventDefault();
        if (!appToOpen.trim() || isOpening) return;
        setIsOpening(true);
        try {
            await DesktopService.sendDesktopCommand(targetUid, targetDeviceId, {
                command: 'open_app',
                app_name: appToOpen.trim()
            });
            setAppToOpen('');
            if (onCommandSent) onCommandSent();
        } catch (err) {
            console.error("Failed to open app:", err);
        } finally {
            setIsOpening(false);
        }
    };

    const handleManualCapture = async () => {
        try {
            await DesktopService.sendDesktopCommand(targetUid, targetDeviceId, {
                command: 'manual_capture'
            });
        } catch (err) {
            console.error("Failed to trigger capture:", err);
        }
    };

    const handleMacro = async (actions) => {
        if (!focusedApp || isSendingMacro) return;
        setIsSendingMacro(true);
        try {
            await DesktopService.sendDesktopCommand(targetUid, targetDeviceId, {
                command: 'run_macro',
                app_name: focusedApp,
                actions: actions
            });
            if (onCommandSent) onCommandSent();
        } catch (err) {
            console.error("Failed to run macro:", err);
        } finally {
            setIsSendingMacro(false);
        }
    };

    const handleCustomType = async (e) => {
        e.preventDefault();
        if (!focusedApp || !customText.trim() || isSendingMacro) return;
        
        setIsSendingMacro(true);
        try {
            await DesktopService.sendDesktopCommand(targetUid, targetDeviceId, {
                command: 'run_macro',
                app_name: focusedApp,
                actions: [
                    { type: 'type', text: customText.trim() },
                    { type: 'press', key: 'enter' }
                ]
            });
            setCustomText('');
            if (onCommandSent) onCommandSent();
        } catch (err) {
            console.error("Failed to type text:", err);
        } finally {
            setIsSendingMacro(false);
        }
    };

    return (
        <div className="p-6 flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Quick Actions */}
            <div className="flex items-center gap-3">
                <button
                    onClick={handleManualCapture}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 rounded-xl hover:bg-indigo-500/20 transition-all font-bold text-xs uppercase tracking-widest"
                >
                    <CameraIcon className="w-4 h-4" />
                    Snap Screenshot
                </button>
                <button
                    onClick={fetchWindows}
                    disabled={isLoadingWindows}
                    className="flex items-center gap-2 px-4 py-2 bg-white/5 text-[var(--text-muted)] border border-white/10 rounded-xl hover:bg-white/10 transition-all font-bold text-xs uppercase tracking-widest disabled:opacity-50"
                >
                    <RefreshIcon className={`w-4 h-4 ${isLoadingWindows ? 'animate-spin' : ''}`} />
                    Refresh Windows
                </button>
            </div>

            {/* Window Switcher */}
            <div>
                <h4 className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-dim)] font-bold mb-4 flex items-center gap-2">
                    <WindowIcon className="w-3.5 h-3.5" />
                    Switch Window
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {windows.length === 0 && !isLoadingWindows ? (
                        <p className="text-[10px] text-[var(--text-dim)] italic col-span-full">No visible windows found.</p>
                    ) : (
                        windows.map((app) => {
                            const isFocused = focusedApp === app;
                            return (
                                <button
                                    key={app}
                                    onClick={() => handleFocusWindow(app)}
                                    className={`px-3 py-2 border rounded-xl transition-all text-left group relative overflow-hidden ${
                                        isFocused 
                                            ? 'bg-indigo-500/20 border-indigo-500/50 shadow-[0_0_15px_rgba(99,102,241,0.2)]' 
                                            : 'bg-[var(--bg-tertiary)] border-[var(--border-subtle)] hover:border-indigo-500/40 hover:bg-indigo-500/5'
                                    }`}
                                >
                                    {isFocused && <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500 rounded-l-xl"></div>}
                                    <span className={`text-[11px] font-bold truncate block transition-colors ${
                                        isFocused ? 'text-indigo-300' : 'text-[var(--text-main)] group-hover:text-indigo-400'
                                    }`}>
                                        {app}
                                    </span>
                                </button>
                            );
                        })
                    )}
                </div>
            </div>

            {/* App specific commands */}
            {focusedApp && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                    <h4 className="text-[10px] uppercase tracking-[0.2em] text-indigo-400/80 font-bold mb-4 flex items-center gap-2">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                        Controls for {focusedApp}
                    </h4>
                    
                    <div className="flex flex-col gap-3">
                        {PRESET_MACROS[focusedApp] && (
                            <div className="flex flex-wrap gap-2">
                                {PRESET_MACROS[focusedApp].map((macro, i) => (
                                    <button
                                        key={i}
                                        onClick={() => handleMacro(macro.actions)}
                                        disabled={isSendingMacro}
                                        className="px-4 py-2 bg-indigo-600/20 text-indigo-300 border border-indigo-500/30 rounded-xl text-xs font-bold hover:bg-indigo-600/30 transition-all disabled:opacity-50 flex items-center gap-2"
                                    >
                                        {isSendingMacro ? 'Sending...' : macro.label}
                                    </button>
                                ))}
                            </div>
                        )}
                        
                        <form onSubmit={handleCustomType} className="flex gap-2">
                            <input
                                type="text"
                                value={customText}
                                onChange={(e) => setCustomText(e.target.value)}
                                placeholder="Type and hit enter..."
                                disabled={isSendingMacro}
                                className="flex-1 bg-black/20 border border-indigo-500/20 rounded-xl px-4 py-2 text-xs text-[var(--text-main)] focus:outline-none focus:border-indigo-500/50 transition-all disabled:opacity-50"
                            />
                            <button
                                type="submit"
                                disabled={!customText.trim() || isSendingMacro}
                                className="px-6 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-indigo-500 transition-all disabled:opacity-50"
                            >
                                Send
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Launch App */}
            <div>
                <h4 className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-dim)] font-bold mb-4 flex items-center gap-2">
                    <RocketIcon className="w-3.5 h-3.5" />
                    Launch Application
                </h4>
                <form onSubmit={handleOpenApp} className="flex gap-2">
                    <input
                        type="text"
                        value={appToOpen}
                        onChange={(e) => setAppToOpen(e.target.value)}
                        placeholder="e.g. Chrome, Slack, VS Code..."
                        className="flex-1 bg-black/20 border border-white/5 rounded-xl px-4 py-2 text-xs text-[var(--text-main)] focus:outline-none focus:border-indigo-500/40 transition-all"
                    />
                    <button
                        type="submit"
                        disabled={!appToOpen.trim() || isOpening}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-indigo-500 transition-all disabled:opacity-50"
                    >
                        {isOpening ? 'Launching...' : 'Launch'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default LiveInsightControl;
